import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { oportunidadeService } from '@/services/oportunidadeService'
import { curadoriaService } from '@/services/curadoriaService'
import { estruturacaoConsultorService, ehSemPermissao } from '@/services/estruturacaoConsultorService'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import { Oportunidade } from '@/types/oportunidade'
import { DefesaRastreabilidade, StatusDefesa } from '@/types/estruturacao'
import { formatCNPJ, formatCurrency, formatDateTime } from '@/utils/formatters'

/**
 * Defesa de Crédito — aprovação (Onda 6 / A11), sobre o `statusDefesa` do A08 (V41):
 * MINUTA → EM_REVISAO (`Enviar para revisão`, OPERADOR+) → APROVADA (`Aprovar defesa`,
 * ADMIN/GERENTE — o backend responde 403 aos demais e grava o autor do `sub`).
 * `Editar minuta` leva à página de redação que já existe (`/reservas/:id/defesa`).
 *
 * A minuta é do back-office: o CONSULTOR não vê esta página (o detalhe nem devolve os
 * textos da defesa para ele) — acompanha pela rastreabilidade no workspace de estruturação.
 */

const ROTULO: Record<StatusDefesa, string> = {
  MINUTA: 'Em preparação',
  EM_REVISAO: 'Em revisão',
  APROVADA: 'Aprovada',
}
const COR: Record<StatusDefesa, 'default' | 'warning' | 'success'> = {
  MINUTA: 'default',
  EM_REVISAO: 'warning',
  APROVADA: 'success',
}

const temMinuta = (o: Oportunidade): boolean =>
  !!(o.analiseFinanceira || o.teseCreditoFundamento || o.avaliacaoCedente || o.recomendacao)

const Bloco: FC<{ titulo: string; texto?: string | null }> = ({ titulo, texto }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline">{titulo}</Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {texto?.trim() ? texto : '—'}
    </Typography>
  </Box>
)

const DefesaCreditoAprovacaoPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  const gestor = papel === 'ADMIN' || papel === 'GERENTE'

  const [reserva, setReserva] = useState<Oportunidade | null>(null)
  const [rastro, setRastro] = useState<DefesaRastreabilidade | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)
  const [agindo, setAgindo] = useState(false)

  const carregar = useCallback(async () => {
    if (!id) return
    setCarregando(true)
    setErro(null)
    setSemPermissao(false)
    try {
      const [o, r] = await Promise.all([
        oportunidadeService.buscar(id),
        estruturacaoConsultorService.rastreabilidade(id).catch(() => null),
      ])
      setReserva(o)
      setRastro(r)
    } catch (e) {
      if (ehSemPermissao(e)) setSemPermissao(true)
      else setErro((e as { message?: string } | null)?.message || 'Não foi possível carregar a defesa.')
    } finally {
      setCarregando(false)
    }
  }, [id])

  useEffect(() => {
    if (backoffice) void carregar()
  }, [backoffice, carregar])

  const executar = async (acao: 'revisao' | 'aprovar') => {
    if (!id) return
    setAgindo(true)
    try {
      const atualizada =
        acao === 'revisao'
          ? await curadoriaService.enviarDefesaParaRevisao(id)
          : await curadoriaService.aprovarDefesa(id)
      setReserva((prev) => (prev ? { ...prev, ...atualizada } : atualizada))
      enqueueSnackbar(
        acao === 'revisao' ? 'Defesa enviada para revisão.' : 'Defesa aprovada.',
        { variant: 'success' }
      )
      // a linha do tempo muda com a aprovação
      setRastro(await estruturacaoConsultorService.rastreabilidade(id).catch(() => null))
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      enqueueSnackbar(msg || 'Não foi possível concluir a ação.', { variant: 'error' })
    } finally {
      setAgindo(false)
    }
  }

  if (!backoffice) {
    return (
      <Box>
        <PageHeader title="Defesa de Crédito" />
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A minuta é do back-office. Acompanhe a defesa pela rastreabilidade no workspace de estruturação da reserva."
        />
      </Box>
    )
  }

  const status = reserva?.statusDefesa ?? null
  const podeEnviarRevisao = !!reserva && (status === null || status === 'MINUTA') && temMinuta(reserva)
  const podeAprovar = gestor && status === 'EM_REVISAO'

  return (
    <Box>
      <PageHeader
        title="Defesa de Crédito"
        subtitle={
          reserva
            ? `${reserva.razaoSocial ?? reserva.nomeFantasia ?? 'Empresa'} · ${formatCNPJ(reserva.cnpj)} · ${formatCurrency(reserva.valorEstimado)}`
            : 'Revisão e aprovação da minuta'
        }
        acao={
          reserva ? (
            <Stack direction="row" spacing={1}>
              {status !== 'APROVADA' && (
                <Button variant="outlined" onClick={() => navigate(`/reservas/${id}/defesa`)}>
                  Editar minuta
                </Button>
              )}
              {status !== 'EM_REVISAO' && status !== 'APROVADA' && (
                <Button
                  variant="contained"
                  disabled={!podeEnviarRevisao || agindo}
                  onClick={() => void executar('revisao')}
                >
                  Enviar para revisão
                </Button>
              )}
              {status === 'EM_REVISAO' && (
                <Button
                  variant="contained"
                  color="success"
                  disabled={!podeAprovar || agindo}
                  onClick={() => void executar('aprovar')}
                >
                  Aprovar defesa
                </Button>
              )}
            </Stack>
          ) : undefined
        }
      />

      {carregando ? (
        <EstadoDeLista estado="carregando" />
      ) : semPermissao ? (
        <EstadoDeLista estado="semPermissao" />
      ) : erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : reserva ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          <Card>
            <CardHeader
              title="Minuta"
              action={
                <Chip
                  size="small"
                  color={status ? COR[status] : 'default'}
                  label={status ? ROTULO[status] : 'Não iniciada'}
                  data-testid="chip-status-defesa"
                />
              }
            />
            <CardContent>
              {!temMinuta(reserva) ? (
                <Alert severity="info">
                  A minuta ainda não foi redigida — use <strong>Editar minuta</strong> para começar.
                  Só uma minuta com conteúdo pode ir para revisão.
                </Alert>
              ) : (
                <>
                  <Bloco titulo="Análise financeira" texto={reserva.analiseFinanceira} />
                  <Bloco titulo="Tese de crédito" texto={reserva.teseCreditoFundamento} />
                  <Bloco titulo="Avaliação do cedente" texto={reserva.avaliacaoCedente} />
                  <Bloco titulo="Recomendação" texto={reserva.recomendacao} />
                  {(reserva.pontosPositivos?.length || reserva.pontosAtencao?.length) ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="overline">Pontos positivos</Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {(reserva.pontosPositivos ?? []).map((p) => (
                              <li key={p}>
                                <Typography variant="body2">{p}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="overline">Pontos de atenção</Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {(reserva.pontosAtencao ?? []).map((p) => (
                              <li key={p}>
                                <Typography variant="body2">{p}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      </Stack>
                    </>
                  ) : null}
                </>
              )}
              {status === 'EM_REVISAO' && !gestor && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Aguardando aprovação de ADMIN ou GERENTE.
                </Alert>
              )}
              {status === 'APROVADA' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Aprovada
                  {reserva.defesaAprovadaEm ? ` em ${formatDateTime(reserva.defesaAprovadaEm)}` : ''}
                  {reserva.defesaAprovadaPor ? ` por ${reserva.defesaAprovadaPor}` : ''}.
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Rastreabilidade" subheader="O que o consultor vê — nunca a minuta" />
            <CardContent>
              {rastro ? (
                <List dense disablePadding aria-label="Rastreabilidade da defesa">
                  {rastro.etapas.map((e) => (
                    <ListItem key={e.codigo} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {e.concluida ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={e.rotulo}
                        secondary={e.data ? formatDateTime(e.data) : e.concluida ? 'concluída' : 'pendente'}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Rastreabilidade indisponível.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      ) : null}
    </Box>
  )
}

export default DefesaCreditoAprovacaoPage
